import React from 'react'
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import {
    SortableContext,
    useSortable,
    arrayMove,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

const FileReorder = ({
    props,
    formik,
    removeFile,
    viewFile,
    handleImageEditModal,
}) => {
    // Sensors for drag-and-drop
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                delay: 100, // Delay before drag starts
                tolerance: 5,
            },
        })
    )

    // Handle drag end event
    const onDragEnd = (event) => {
        const { active, over } = event

        if (over && active.id !== over.id) {
            const from = formik.values[props.name].findIndex(
                (file) => file === active.id
            )
            const to = formik.values[props.name].findIndex(
                (file) => file === over.id
            )

            if (from !== -1 && to !== -1) {
                const reorderedItems = arrayMove(
                    formik.values[props.name],
                    from,
                    to
                )
                formik.setFieldValue(props.name, reorderedItems)
            }
        }
    }

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
        >
            <SortableContext
                items={formik.values[props.name]}
                strategy={verticalListSortingStrategy}
            >
                <div className="reorderCnt">
                    {formik.values[props.name]?.map((file, index) => (
                        <SortableItem
                            key={file}
                            id={file}
                            file={file}
                            index={index}
                            props={props}
                            removeFile={removeFile}
                            viewFile={viewFile}
                            handleImageEditModal={handleImageEditModal}
                        />
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    )
}

const SortableItem = ({
    id,
    file,
    index,
    props,
    removeFile,
    viewFile,
    handleImageEditModal,
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id })

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        cursor: 'grab',
    }

    const getFileExtension = (fileName) =>
        fileName.split('.').pop().toLowerCase()
    const isValidHttpUrl = (url) => {
        try {
            new URL(url)
            return true
        } catch {
            return false
        }
    }

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className="thumb"
        >
            <div className="thumbInner">
                {['png', 'jpg', 'webp', 'jpeg'].includes(
                    getFileExtension(file)
                ) ? (
                    <div className="thumbCnt">
                        {isValidHttpUrl(file) ? (
                            <img
                                src={file}
                                className="img"
                                id={`thumb_${index}`}
                                alt="File Thumbnail"
                            />
                        ) : (
                            <img
                                src={`${
                                    global.customImgurl
                                        ? `${
                                              process.env.REACT_APP_IMAGE_URL
                                          }/uploads/${
                                              props.isCompress
                                                  ? `${props.folder}/compressed`
                                                  : props.folder
                                          }/${file}`
                                        : `${global.site_url}/uploads/${
                                              props.isCompress
                                                  ? `${props.folder}/compressed`
                                                  : props.folder
                                          }/${file}`
                                }`}
                                className="img"
                                id={`thumb_${index}`}
                                alt="File Thumbnail"
                            />
                        )}
                        <div className="fileActions">
                            {!props.disableDeleting && (
                                <span
                                    onClick={() =>
                                        removeFile(file, props.name, index)
                                    }
                                    className="cancelBtn material-icons"
                                >
                                    delete
                                </span>
                            )}
                            <span
                                onClick={() => viewFile(file, props.name)}
                                className="viewBtn material-icons"
                            >
                                visibility
                            </span>
                            {!props.disableEditing && (
                                <span
                                    onClick={() =>
                                        handleImageEditModal(true, file, index)
                                    }
                                    className="editBtn material-icons"
                                >
                                    edit
                                </span>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="thumbCnt">
                        <div className="defaultThumb">
                            <span className="material-icons">
                                {getFileExtension(file) === 'pdf'
                                    ? 'picture_as_pdf'
                                    : ['doc', 'docx'].includes(
                                          getFileExtension(file)
                                      )
                                    ? 'description'
                                    : ['mp4', 'mpeg'].includes(
                                          getFileExtension(file)
                                      )
                                    ? 'movie'
                                    : ['mpga', 'mp3'].includes(
                                          getFileExtension(file)
                                      )
                                    ? 'volume_up'
                                    : 'description'}
                            </span>
                        </div>
                        <div className="fileActions">
                            {!props.disableDeleting && (
                                <span
                                    onClick={() =>
                                        removeFile(file, props.name, index)
                                    }
                                    className="cancelBtn material-icons"
                                >
                                    delete
                                </span>
                            )}
                            <span
                                onClick={() => viewFile(file, props.name)}
                                className="viewBtn material-icons"
                            >
                                visibility
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default FileReorder
